//---------------------------------------------------------------------------------------------------
//  ?                              WEBSOCKET MANAGER
//  @author         :  DeeTrade Team  
//  @createdOn      :  06/19/2025
//  @description    :  Central WebSocket connection manager with ultra-fast performance
//---------------------------------------------------------------------------------------------------

import { UltraFastProtocol, MESSAGE_TYPES } from './protocol/UltraFastProtocol';
import { v4 as uuidv4 } from 'uuid';
import { isAnyIndianVenueOpenNow, isTickVenueOpenNow, istTradingDayKey } from '../utils/exchangeHours';

/**
 * #44 V12 (d) — the single source of truth for "how much tick silence counts as
 * a delivery stall".
 *
 * //! V12b F4: this used to be TWO hand-written 45000 literals — one in the
 * //! provider's watchdog (the writer that RAISES 'stalled') and one as this
 * //! function's default parameter (used by the reconciler, the writer that
 * //! HOLDS it). They agreed only by coincidence. Tuning either one alone
 * //! silently reintroduces the V12b defect with the whole suite still green:
 * //! the watchdog raises at its threshold, the reconciler evaluates `stalled`
 * //! at a different one, reads false, and clears the banner within 2s.
 * //! Both writers now import this constant. Do not re-inline it.
 */
export const DELIVERY_STALL_THRESHOLD_MS = 45000;

/**
 * Singleton WebSocket Manager for ultra-fast trading platform
 * Manages single connection per user with channel multiplexing
 * Targets <0.5ms message latency with auto-reconnection
 */
class WebSocketManager {
  constructor() {
    if (WebSocketManager.instance) {
      return WebSocketManager.instance;
    }

    // Connection state
    this.connection = null;
    this.connectionState = 'disconnected'; // disconnected, connecting, connected, error
    this.isAuthenticated = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; //! #44: end of the FAST phase, NOT a give-up cap
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    //! #44: after the fast phase is exhausted we NEVER permanently give up — we
    //! fall back to a slow, indefinite retry so a long outage (e.g. the 3h
    //! partition on 2026-08-03) self-heals without a hard refresh. `isDegraded`
    //! flips true on entry so the UI can surface the "reconnecting" banner.
    this.slowRetryInterval = 30000; // 30s indefinite slow-phase cadence
    this.isDegraded = false;
    this.reconnectTimeout = null; //! single tracked timer — never stack reconnects
    //! #44: transport-open timestamp + how long the auth handshake is allowed to
    //! run before the poll rescue treats it as stuck. See needsRescue().
    this.transportOpenedAt = null;
    this.authGraceMs = 20000;
    //! #44: set by disconnect() so the never-give-up rescue doesn't resurrect a
    //! socket the app closed on purpose. Cleared by connect()/forceReconnectNow().
    this.intentionallyDisconnected = false;
    //! #44: throttle for the token-refresh request raised on auth failure. See
    //! handleAuthError — unbounded it becomes a hot loop against the auth service.
    this.lastTokenRefreshRequestAt = null;
    this.tokenRefreshMinIntervalMs = 30000;
    this.heartbeatInterval = null;
    this.heartbeatTimeout = null;

    //! #44 V12 delivery-health: a socket can be CONNECTED+authenticated yet
    //! deliver zero ticks to its subscribers ("backend green / screen blank").
    //! Stamp the last time real market DATA arrived so the provider watchdog can
    //! detect a delivery stall and self-heal even when the transport looks fine.
    this.lastInboundDataAt = null;   // any DATA frame
    this.lastInboundTickAt = null;   // DATA on a live-tick topic (market.*.tick)
    this.topicLastDataAt = new Map(); // topic -> ts of last DATA frame
    //! V12B-11: topic -> the venue that feeds it ('NSE' | 'MCX' | …), supplied by the
    //! subscriber. A tick topic is `market.{uuid}.tick` and carries no exchange, so
    //! without this hint the stall predicate can only ask "is ANY venue open" — which
    //! is why an NSE-only watchlist false-fired between 15:30 and MCX's 23:30 close.
    this.topicExchange = new Map();
    
    // User identity and session management
    this.authToken = null;
    this.userIdentity = {
      user_id: null,
      device_id: this.generateDeviceId(),
      session_id: uuidv4(),
      window_id: uuidv4()
    };
    
    // Message handling
    this.messageHandlers = new Map(); // channel -> Set of handlers
    this.pendingMessages = [];
    this.messageQueue = [];
    this.protocol = new UltraFastProtocol();
    
    // Performance tracking
    this.metrics = {
      connectTime: null,
      lastLatency: null,
      messagesSent: 0,
      messagesReceived: 0,
      reconnectCount: 0,
      avgLatency: 0,
      latencyHistory: []
    };
    // Configuration
    this.config = {
      url:
        process.env.REACT_APP_WEBSOCKET_URL ||
        // On the HTTPS tunnel (app.deetrade.net) a ws://localhost socket is
        // blocked as mixed content, so live data never loads. Use a SECURE
        // same-origin URL that nginx + the dev-server proxy forward to the
        // backend WS (:8080) via the /rtws path. On http://localhost keep the
        // direct socket (unchanged).
        (typeof window !== 'undefined' && window.location.protocol === 'https:'
          ? `wss://${window.location.host}/rtws`
          : 'ws://localhost:8080'),
      enableBinary: true, // Re-enabled with proper MessagePack format
      enableCompression: true,
      heartbeatInterval: 30000, // 30 seconds
      heartbeatTimeout: 10000,  // 10 seconds
      messageTimeout: 5000,     // 5 seconds
      maxQueueSize: 1000
    };
    /**
     * Efficient topic-based subscription registry
     */
    this.topicHandlers = new Map(); // topic -> Set of handlers
    this.topicCounts = new Map();   // topic -> count of subscribers
    this.subscriptionOptions = new Map(); // topic -> merged options
    
    //! F9: background TRACKING only — we do not pause anything. The old
    //! `pausedSubscriptions` map went with the dead pause loop; it was always
    //! empty. These two feed getBackgroundDuration() and the `tab_foregrounded`
    //! event, which are how a consumer sizes a refetch after a long absence.
    this.backgroundSince = null; // Timestamp when tab went to background
    this.isInBackground = false;

    //! #50-1: mirror of backgroundSince/isInBackground, but for a SOCKET-LEVEL
    //! gap rather than a tab-visibility gap. A backend restart (adapter/BIS/OMS)
    //! drops the transport while the tab stays foregrounded the whole time — no
    //! visibilitychange event ever fires, so `tab_foregrounded` never runs and the
    //! disconnect->reconnect hole (missed ticks/bars) was never repaired. Stamped
    //! in handleDisconnection(), consumed + cleared in handleAuthSuccess().
    this.disconnectedAt = null;
    
    WebSocketManager.instance = this;

    this.ready = false; // General ready state for all pages
    this._readyPromise = null;
    this._readyResolve = null;
  }

  /**
   * Generate or retrieve persistent device ID
   */
  generateDeviceId() {
    let deviceId = localStorage.getItem('deetrade_device_id');
    if (!deviceId) {
      deviceId = `device_${uuidv4()}`;
      localStorage.setItem('deetrade_device_id', deviceId);
    }
    return deviceId;
  }

  /**
   * Set user identity from authentication context with NEW session_id
   */
  setUserIdentity(userId, authToken, websocketSessionId = null) {
    const wasConnected = this.connectionState === 'connected';
    
    this.userIdentity.user_id = userId;
    this.setAuthToken(authToken, websocketSessionId); // Pass the new session_id
    
    // If already connected, refresh with new credentials
    // This is a credential refresh, NOT a reconnect due to connection loss
    if (wasConnected) {
      console.log('🔄 [WebSocketManager] Token/identity changed, refreshing connection...');
      this.refreshConnection();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    console.log('[WebSocketManager] disconnect() called');
    this.stopHeartbeat();
    this.isAuthenticated = false;
    this.intentionallyDisconnected = true; //! #44: keep the rescue out of this
    //! #44: cancel any pending (incl. slow-phase indefinite) reconnect — an
    //! intentional disconnect must NOT be fought by the never-give-up loop.
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.isDegraded = false;
    
    if (this.connection) {
      try {
        if (this.connection.readyState === WebSocket.OPEN || 
            this.connection.readyState === WebSocket.CONNECTING) {
          this.connection.close(1000, 'Client disconnect');
        }
      } catch (e) {
        console.warn('[WebSocketManager] Error closing connection:', e);
      }
      this.connection = null;
    }
    
    this.connectionState = 'disconnected';
    this.transportOpenedAt = null;
    this.ready = false;
    this.notifyListeners('disconnection', { reason: 'client_disconnect' });
  }

  /**
   * Refresh connection with new credentials without counting as a reconnect attempt.
   * Used when access token is silently refreshed.
   */
  async refreshConnection() {
    console.log('🔄 [WebSocketManager] Refreshing connection with new credentials...');
    
    // Reset reconnect attempts for credential refresh (this is intentional, not a failure)
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
    
    // Disconnect cleanly
    this.disconnect();
    
    // Reconnect with new credentials
    try {
      await this.connect();
      console.log('✅ [WebSocketManager] Connection refreshed successfully');
    } catch (error) {
      console.error('❌ [WebSocketManager] Failed to refresh connection:', error);
      this.notifyListeners('error', error);
    }
  }

  /**
   * Set authentication token and use NEW WebSocket session_id
   */
  setAuthToken(token, websocketSessionId = null) {
    this.authToken = token;
    
    // Use the NEW WebSocket session_id if provided, otherwise extract from JWT
    if (websocketSessionId) {
      this.userIdentity.session_id = websocketSessionId;
      console.log('🆔 Using NEW WebSocket session_id:', websocketSessionId);
    } else if (token) {
      // Fallback: Extract session_id from JWT token if new session_id not provided
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.session_id) {
          this.userIdentity.session_id = payload.session_id;
          console.log('🔍 Fallback: Updated session_id from JWT token:', payload.session_id);
        }
        if (payload.sub) {
          this.userIdentity.user_id = payload.sub;
          console.log('🔍 Updated user_id from JWT token:', payload.sub);
        }
      } catch (e) {
        console.warn('⚠️ Could not extract info from JWT token:', e);
      }
    }
    
    console.log('🆔 Final WebSocket session identity:', {
      user_id: this.userIdentity.user_id,
      session_id: this.userIdentity.session_id,
      device_id: this.userIdentity.device_id,
      window_id: this.userIdentity.window_id
    });
  }

  /**
   * Connect to WebSocket server with ultra-fast configuration
   */
  async connect() {
    console.log(`[WebSocketManager] connect() called. State: ${this.connectionState}`);
    //! #44: an explicit request to connect ends any intentional-disconnect state,
    //! so the never-give-up rescue is armed again from here on.
    this.intentionallyDisconnected = false;
    if (this.connectionState === 'connecting' || this.connectionState === 'connected') {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        //! #44: EXACTLY ONE socket per manager. Several paths reach connect() while a
        //! stale socket is still held, and the auth-failure path leaves it OPEN:
        //! handleAuthError does not touch connectionState, then reconnect() flips it
        //! to 'disconnected' WITHOUT closing this.connection — so the assignment below
        //! used to overwrite the reference and ORPHAN a live socket that kept
        //! heart-beating to the server for ever. Measured on one page: 14 sockets
        //! created in a single reconnect, 8 still OPEN at once, server registry at 8
        //! connections for one user. Tear down whatever we hold before opening.
        if (this.connection) {
          try {
            this.connection.onopen = this.connection.onmessage = this.connection.onclose = this.connection.onerror = null;
            this.connection.close();
          } catch (e) { /* already dead — nothing to do */ }
          this.connection = null;
        }

        this.connectionState = 'connecting';
        this.notifyListeners('connection', { state: 'connecting' });
        
        const startTime = performance.now();
        
        console.log(`[WebSocketManager] Connecting to URL: ${this.config.url}`);

        // Create WebSocket connection with optimizations
        // Note: Don't pass token in URL - wait for auth_required message instead
        const ws = new WebSocket(this.config.url);
        this.connection = ws;
        
        // Enable binary frames for MessagePack
        if (this.config.enableBinary) {
          ws.binaryType = 'arraybuffer';
        }

        //! #44: guard the socket-OPEN phase. A socket created during an offline
        //! window can hang in CONNECTING indefinitely (never fires open OR
        //! close), which strands the reconnect loop and pins the banner on
        //! "reconnecting". If it hasn't opened within connectTimeout, tear the
        //! zombie down and schedule a fresh retry so it always makes progress.
        const connectTimeoutId = setTimeout(() => {
          if (this.connection === ws && this.connectionState !== 'connected') {
            console.warn(`[WebSocketManager] connect() stuck in '${this.connectionState}' > ${this.config.connectTimeout || 10000}ms — forcing teardown + retry`);
            try {
              ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
              ws.close();
            } catch (e) { /* ignore */ }
            if (this.connection === ws) {
              this.connection = null;
              this.connectionState = 'disconnected';
            }
            this.scheduleReconnect();
            reject(new Error('connect timeout'));
          }
        }, this.config.connectTimeout || 10000);

        ws.onopen = () => {
          clearTimeout(connectTimeoutId);
          // Ignore if this is not the current connection (race condition fix)
          if (this.connection !== ws) {
            console.log('🛑 [WebSocketManager] Ignoring onopen from stale connection');
            ws.close();
            return;
          }

          this.metrics.connectTime = performance.now() - startTime;
          this.connectionState = 'connected';
          //! #44: stamp the moment the TRANSPORT opened. Auth is a second,
          //! untimed round-trip (auth_required -> handshake -> auth_success), so
          //! 'connected && !isAuthenticated' is a legitimate in-flight state.
          //! needsRescue() uses this to leave the handshake alone until the
          //! grace window is spent instead of tearing it down mid-flight.
          this.transportOpenedAt = Date.now();
          // Don't reset reconnectAttempts here - only reset after successful auth
          // this.reconnectAttempts = 0;
          // this.reconnectDelay = 1000;
          
          console.log('🌐 WebSocket connection opened, waiting for auth_required...');
          
          // Don't send handshake yet - wait for auth_required message first
          // this.sendHandshake(); // This will be called after auth_success
          
          // Start heartbeat after authentication
          // this.startHeartbeat(); // This will be called after auth_success
          
          this.notifyListeners('connection', { 
            state: 'connected', 
            connectTime: this.metrics.connectTime 
          });
          
          resolve();
        };

        ws.onmessage = (event) => {
          // Ignore if this is not the current connection (race condition fix)
          if (this.connection !== ws) return;
          this.handleMessage(event);
        };

        ws.onclose = (event) => {
          clearTimeout(connectTimeoutId);
          // Ignore if this is not the current connection (race condition fix)
          if (this.connection !== ws) return;

          this.handleDisconnection(event);
          if (this.connectionState === 'connecting') {
            reject(new Error(`Connection failed: ${event.reason}`));
          }
        };

        ws.onerror = (error) => {
          clearTimeout(connectTimeoutId);
          // Ignore if this is not the current connection (race condition fix)
          if (this.connection !== ws) return;

          this.handleError(error);
          if (this.connectionState === 'connecting') {
            reject(error);
          }
        };

      } catch (error) {
        this.connectionState = 'error';
        this.notifyListeners('error', error);
        reject(error);
      }
    });
  }

  /**
   * Send hierarchical identity detail (BE-001 protocol)
   */
  sendIdentity() {
    // Use protocol string type so UltraFastProtocol maps to numeric code
    const identityMessage = this.protocol.createMessage({
      type: 'identity',
      device_id: this.userIdentity.device_id,
      window_id: this.userIdentity.window_id,
      page_id: this.generatePageId(), // Generate page-specific ID
      timestamp: Date.now()
    });
    this.sendMessage(identityMessage);
  }

  /**
   * Generate page-specific identifier
   */
  generatePageId() {
    // Use current page path and timestamp for uniqueness
    const path = window.location.pathname.replace(/\//g, '_') || 'root';
    const timestamp = Date.now().toString(36);
    return `page_${path}_${timestamp}`;
  }

  /**
   * Subscribe to a topic with event handlers (data, ack, error)
   * @param {string} topic - The topic to subscribe to
   * @param {object|function} handlerOrObj - Handler object { onData, onAck, onError } or function (legacy: onData)
   * @param {object} [options] - Subscription options forwarded to the backend, PLUS the
   *   client-only `exchange` hint (V12B-11) naming the venue that feeds this topic.
   * @returns {function} Unsubscribe function
   */
  subscribeTopic(topic, handlerOrObj, options = {}) {
    //! V12B-11: `exchange` is CLIENT-SIDE metadata for the delivery-stall predicate, not
    //! part of the backend's subscribe contract. Split it off here — in ONE place, before
    //! anything is stored — so it can never reach the wire via either sender (this method
    //! or resubscribeAll, which replays subscriptionOptions verbatim after a reconnect).
    //! Silently growing a wire payload is how a frontend-only change breaks a service.
    const { exchange: exchangeHint, ...wireOptions } = options || {};
    if (exchangeHint) {
      this.topicExchange.set(topic, String(exchangeHint).trim().toUpperCase());
    }

    console.log(`📡 [WebSocketManager] subscribeTopic called: ${topic}, options: ${JSON.stringify(wireOptions)}, connectionState: ${this.connectionState}, isAuthenticated: ${this.isAuthenticated}`);
    if (!this.topicHandlers.has(topic)) {
      this.topicHandlers.set(topic, new Set());
      this.topicCounts.set(topic, 0);
    }

    // Merge new options with existing options for this topic
    const currentOptions = this.subscriptionOptions.get(topic) || {};
    const newOptions = { ...currentOptions, ...wireOptions };
    this.subscriptionOptions.set(topic, newOptions);
    
    // Normalize handler
    let handlerObj;
    if (typeof handlerOrObj === 'function') {
      handlerObj = { onData: handlerOrObj };
    } else {
      handlerObj = handlerOrObj;
    }
    this.topicHandlers.get(topic).add(handlerObj);
    const prevCount = this.topicCounts.get(topic);
    this.topicCounts.set(topic, prevCount + 1);
    // Only send subscribe to backend if this is the first subscriber AND authenticated
    console.log(`📡 [WebSocketManager] Topic ${topic} count: ${prevCount} -> ${prevCount + 1}`);
    //! D41: a LATER subscriber WIDENING options on an already-held topic (e.g. HQ:true
    //!  where the currently-held options have none) must also reach the wire — the first
    //!  subscriber to register a topic is not necessarily the one that needs HQ.
    const widened = wireOptions.HQ && !currentOptions.HQ;
    if (prevCount === 0 || widened) {
      // Only send subscription if connected AND authenticated
      // Otherwise, resubscribeAll() will send it after authentication completes
      if (this.connectionState === 'connected' && this.isAuthenticated) {
        console.log(`📡 [WebSocketManager] Sending SUBSCRIBE for ${topic} with options:`, newOptions);
        // Merge options into the "d" payload alongside "subscription"
        this.sendMessage({ t: MESSAGE_TYPES.SUBSCRIBE, d: { subscription: topic, ...newOptions } });
      } else {
        console.log(`📡 [WebSocketManager] Deferring SUBSCRIBE for ${topic} until authenticated (connectionState: ${this.connectionState}, isAuthenticated: ${this.isAuthenticated})`);
        // Options are stored in this.subscriptionOptions and will be used in resubscribeAll
      }
    }
    // Return unsubscribe function
    return () => this.unsubscribeTopic(topic, handlerObj);
  }

  /**
   * Unsubscribe a specific handler from a topic
   */
  unsubscribeTopic(topic, handlerObj) {
    const handlers = this.topicHandlers.get(topic);
    if (!handlers) {
      return;
    }

    let removedCount = 0;

    // Support both the normalized handler object returned by subscribeTopic()
    // and direct callback functions used by older call sites.
    if (handlers.delete(handlerObj)) {
      removedCount += 1;
    } else {
      Array.from(handlers).forEach(existingHandler => {
        const isDirectFunctionMatch = typeof handlerObj === 'function' && existingHandler?.onData === handlerObj;
        const isEquivalentHandlerObject =
          handlerObj &&
          typeof handlerObj === 'object' &&
          existingHandler?.onData === handlerObj.onData &&
          existingHandler?.onAck === handlerObj.onAck &&
          existingHandler?.onError === handlerObj.onError;

        if (isDirectFunctionMatch || isEquivalentHandlerObject) {
          handlers.delete(existingHandler);
          removedCount += 1;
        }
      });
    }

    const newCount = handlers.size;
    this.topicCounts.set(topic, newCount);
    console.log(`📡 [WebSocketManager] unsubscribeTopic called: ${topic}, removed=${removedCount}, remaining=${newCount}`);

    // Only send unsubscribe to backend if this was the last subscriber
    if (newCount === 0) {
      this.topicHandlers.delete(topic);
      this.topicCounts.delete(topic);
      this.subscriptionOptions.delete(topic); // Clean up options
      //! V12B-11: the venue hint and the delivery baseline are scoped to the SUBSCRIPTION,
      //! not to the topic string. Leaving the timestamp behind means a later re-subscribe
      //! inherits the old one and is judged stalled the instant it is created, before a
      //! single tick could arrive — a self-inflicted false alarm on every re-subscribe
      //! (watchlist edit, contract rollover). Dropping it restores the self-calibrating
      //! property: a fresh subscription has no baseline, so it cannot stall.
      this.topicExchange.delete(topic);
      this.topicLastDataAt.delete(topic);
      this.sendMessage({ t: MESSAGE_TYPES.UNSUBSCRIBE, d: { subscription: topic } });
    }
  }

  /**
   * Register a handler for all responses/errors with a given page_id
   * @param {string} pageId
   * @param {function} handler (msg) => void
   */
  registerPageHandler(pageId, handler) {
    if (!this.pageHandlers) this.pageHandlers = new Map();
    if (!this.pageHandlers.has(pageId)) {
      this.pageHandlers.set(pageId, new Set());
    }
    this.pageHandlers.get(pageId).add(handler);
  }

  /**
   * Unregister a handler for a given page_id
   */
  unregisterPageHandler(pageId, handler) {
    if (this.pageHandlers && this.pageHandlers.has(pageId)) {
      this.pageHandlers.get(pageId).delete(handler);
      if (this.pageHandlers.get(pageId).size === 0) {
        this.pageHandlers.delete(pageId);
      }
    }
  }

  /**
   * Send a one-off request and await response/error by msg_id (optionally page_id)
   * @param {object} message - The message to send (will be assigned a unique msg_id if not present)
   * @param {object} [options] - { pageId, onPageResponse }
   * @returns {Promise} Resolves with response, rejects with error
   */
  sendRequest(message, options = {}) {
    if (!this.pendingRequests) this.pendingRequests = new Map();
    let msgId = message.id || message.msg_id;
    if (!msgId) {
      msgId = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      message.id = msgId;
    }
    // Optionally allow pageId-based handler for this request
    if (options.pageId && typeof options.onPageResponse === 'function') {
      this.registerPageHandler(options.pageId, options.onPageResponse);
    }
    return new Promise((resolve, reject) => {
      this.pendingRequests.set(msgId, { resolve, reject });
      this.sendMessage(message);
      // Optional: timeout for response
      setTimeout(() => {
        if (this.pendingRequests.has(msgId)) {
          this.pendingRequests.delete(msgId);
          if (options.pageId && typeof options.onPageResponse === 'function') {
            this.unregisterPageHandler(options.pageId, options.onPageResponse);
          }
          reject(new Error('Request timed out'));
        }
      }, this.config.messageTimeout || 5000);
    });
  }

  /**
   * Efficiently dispatch topic-based messages (numeric protocol)
   */
  handleMessage(event) {
    const receiveTime = performance.now();
    try {
      let message;
      if (event.data instanceof ArrayBuffer) {
        message = this.protocol.parseMessage(event.data, 'binary');
      } else {
        message = this.protocol.parseMessage(event.data, 'text');
      }
      this.metrics.messagesReceived++;
      const type = message.t;
      const data = message.d;
      
      // console.log(`📥 [WebSocketManager] Received message: type=${type} (${this.protocol.getMessageTypeName(type)})`, data);
      // console.log(`📥 [WebSocketManager] Received message: type=${type} (${this.protocol.getMessageTypeName(type)}) size=${JSON.stringify(data).length}`);

      // Handle request/response by msg_id
      const msgId = message.id || message.msg_id;
      if (msgId && this.pendingRequests && this.pendingRequests.has(msgId)) {
        const { resolve, reject } = this.pendingRequests.get(msgId);
        this.pendingRequests.delete(msgId);
        if (type === MESSAGE_TYPES.ERROR || message.type === 'error') {
          console.error('❌ [WebSocketManager] Request failed:', data);
          reject(data || message);
        } else {
          resolve(data || message);
        }
        this.notifyListeners('message', message);
        this.notifyListeners('raw_message', message);
        return;
      }
      // Route by page_id if present
      const pageId = message.page_id || (data && data.page_id);
      if (pageId && this.pageHandlers && this.pageHandlers.has(pageId)) {
        this.pageHandlers.get(pageId).forEach(fn => {
          try { fn(message); } catch (e) { console.error('Page handler error:', e); }
        });
        this.notifyListeners('message', message);
        this.notifyListeners('raw_message', message);
        return;
      }
      // Handle authentication messages
      if (type === MESSAGE_TYPES.AUTH_REQUIRED) {
        this.handleAuthRequired(message);
        return;
      }
      if (type === MESSAGE_TYPES.AUTH_SUCCESS) {
        this.handleAuthSuccess(message);
        return;
      }
      if (type === MESSAGE_TYPES.AUTH_ERROR) {
        this.handleAuthError(message);
        return;
      }
      // Handle system messages
      if (type === MESSAGE_TYPES.PONG) {
        this.handlePong(message, receiveTime);
        return;
      }
      if (type === MESSAGE_TYPES.IDENTITY_ACK) {
        this.handleIdentitySuccess(message);
        return;
      }
      // Efficient topic-based dispatch (data, ack, error) with wildcard support
      if ((type === MESSAGE_TYPES.DATA || type === MESSAGE_TYPES.SUBSCRIPTION_ACK || type === MESSAGE_TYPES.ERROR || type === MESSAGE_TYPES.SUBSCRIPTION_ERROR) && data?.subscription) {
        const topic = data.subscription;

        //! #44 V12 delivery-health: stamp real DATA arrival per topic so the
        //! provider watchdog can catch a "connected but no ticks" stall. Only
        //! DATA (not ack/error) counts as delivery.
        if (type === MESSAGE_TYPES.DATA) {
          const nowTs = Date.now();
          this.lastInboundDataAt = nowTs;
          this.topicLastDataAt.set(topic, nowTs);
          if (WebSocketManager.isLiveTickTopic(topic)) {
            this.lastInboundTickAt = nowTs;
          }
        }

        // Collect all matching handlers (exact match + wildcard matches)
        const matchingHandlers = [];
        
        // 1. Exact match first
        const exactHandlers = this.topicHandlers.get(topic);
        if (exactHandlers) {
          exactHandlers.forEach(h => matchingHandlers.push(h));
        }
        
        // 2. Check for wildcard subscriptions that match this topic
        // e.g., if topic is 'instrument-sync.abc123', also notify handlers for 'instrument-sync.*'
        if (topic.includes('.')) {
          const prefix = topic.substring(0, topic.lastIndexOf('.')); // 'instrument-sync'
          const wildcardTopic = `${prefix}.*`;
          const wildcardHandlers = this.topicHandlers.get(wildcardTopic);
          if (wildcardHandlers) {
            wildcardHandlers.forEach(h => matchingHandlers.push(h));
            console.log(`🎯 [Wildcard] Topic '${topic}' matched wildcard '${wildcardTopic}', handlers: ${wildcardHandlers.size}`);
          }
        }
        
        // Dispatch to all matching handlers
        if (matchingHandlers.length > 0) {
          matchingHandlers.forEach(handlerObj => {
            try {
              // For DATA messages, check if payload is nested in data.data (standard format)
              // or directly in data (legacy/flat format from some services)
              // This ensures backward compatibility with existing handlers
              const payload = data.data !== undefined ? data.data : data;
              if (type === MESSAGE_TYPES.DATA && handlerObj.onData) handlerObj.onData(payload, message);
              if (type === MESSAGE_TYPES.SUBSCRIPTION_ACK && handlerObj.onAck) handlerObj.onAck(data, message);
              if ((type === MESSAGE_TYPES.ERROR || type === MESSAGE_TYPES.SUBSCRIPTION_ERROR) && handlerObj.onError) handlerObj.onError(data, message);
            } catch (e) {
              console.error('Topic handler error:', e);
            }
          });
        }
        this.notifyListeners('message', message);
        return;
      }
      // Route message to channel handlers (legacy/fallback)
      const channel = message.channel || 'default';
      const handlers = this.messageHandlers.get(channel);
      if (handlers) {
        handlers.forEach(handler => {
          try {
            handler(message);
          } catch (error) {
            console.error('WebSocket message handler error:', error);
          }
        });
      }
      this.notifyListeners('message', message);
      this.notifyListeners('raw_message', message);
    } catch (error) {
      console.error('WebSocket message parsing error:', error);
      this.notifyListeners('error', error);
    }
  }

  /**
   * Handle auth_required message from server
   */
  handleAuthRequired(message) {
    console.log('🔐 Server requesting authentication...');
    
    if (!this.authToken) {
      console.error('❌ No authentication token available');
      this.handleAuthError({ message: 'No authentication token available' });
      return;
    }
    
    // Extract session_id from JWT token
    let sessionIdFromToken = null;
    try {
      const payload = JSON.parse(atob(this.authToken.split('.')[1]));
      sessionIdFromToken = payload.session_id;
      console.log('🔍 Extracted session_id from JWT token:', sessionIdFromToken);
    } catch (e) {
      console.warn('⚠️ Could not extract session_id from JWT token:', e);
    }
    
    // Prepare the data payload for authentication
    const authPayload = {
      token: this.authToken,
      timestamp: Date.now(),
      session_id: sessionIdFromToken || this.userIdentity.session_id
    };
    // Use protocol to wrap the message in the correct format
    const authMessage = this.protocol.createMessage({
      type: 'auth',
      ...authPayload
    });
    
    console.log('📤 Sending authentication message with session_id:', authPayload.session_id);
    this.sendMessage(authMessage);
  }

  /**
   * Handle successful authentication
   */
  handleAuthSuccess(message) {
    console.log('✅ WebSocket authentication successful!');
    this.isAuthenticated = true;
    
    // Reset reconnect counter only after successful authentication
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
    //! #44: clear the degraded/slow-retry state and any pending reconnect timer
    //! now that we're healthy again, so the UI banner clears and the next drop
    //! starts from the fast phase.
    this.isDegraded = false;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    //! #44: credentials just worked, so clear the token-refresh throttle — a genuine
    //! expiry later must be able to ask for a fresh token immediately rather than
    //! serve out a stale cooldown from an unrelated earlier failure.
    this.lastTokenRefreshRequestAt = null;
    this.notifyListeners('reconnected', { at: Date.now() });

    // Send identity after successful authentication
    this.sendIdentity();
    
    // Start heartbeat now that we're authenticated
    this.startHeartbeat();
    
    // Process pending messages
    this.processPendingMessages();
    
    // Resubscribe to all active topics (in case of reconnection)
    this.resubscribeAll();

    //! #50-1: transport+auth just came back — if a real gap was open (socket
    //! dropped while the tab stayed foregrounded, e.g. a backend restart), tell
    //! subscribers exactly how long it was so they can repair it. Same event
    //! shape as `tab_foregrounded` ({backgroundDuration, topics}) so consumers
    //! (useChartData's refetchMissedRange) run through the ONE existing repair
    //! path instead of a second mechanism. Never fires for an intentional
    //! disconnect() (that path never sets disconnectedAt's counterpart here —
    //! intentionallyDisconnected short-circuits reconnect() entirely).
    if (this.disconnectedAt != null) {
      const backgroundDuration = Date.now() - this.disconnectedAt;
      this.disconnectedAt = null;
      this.notifyListeners('ws_reconnected_after_gap', {
        backgroundDuration,
        topics: Array.from(this.topicHandlers.keys()),
      });
    }

    this.notifyListeners('auth_success', message);
  }

  /**
   * Resubscribe to all active topics after reconnection
   */
  /**
   * Resubscribe to all active topics after reconnection
   */
  resubscribeAll() {
    if (this.topicHandlers.size > 0) {
      console.log(`🔄 [WebSocketManager] Resubscribing to ${this.topicHandlers.size} active topics...`);
      this.topicHandlers.forEach((handlers, topic) => {
        if (handlers.size > 0) {
          console.log(`🔄 [WebSocketManager] Resubscribing to ${topic}`);
          const options = this.subscriptionOptions.get(topic) || {};
          this.sendMessage({ t: MESSAGE_TYPES.SUBSCRIBE, d: { subscription: topic, ...options } });
        }
      });
    }
  }

  /**
   * Handle authentication error
   */
  handleAuthError(message) {
    console.error('❌ WebSocket authentication failed:', message);
    console.log(`🔄 Reconnect attempts: ${this.reconnectAttempts} (fast budget ${this.maxReconnectAttempts})`);
    this.isAuthenticated = false;

    //! #44 review follow-up: "never give up" is only worth anything if the NEXT
    //! attempt can differ from this one. Auth failed, and nothing else in the app
    //! refreshes the WS token on its own — WebSocketProvider has a
    //! `token_refresh_needed` handler ready but, until now, NOTHING emitted it, so
    //! that handler was dead code. Without this the removal of the old max-attempts
    //! give-up turns a stale token into an INFINITE futile retry: same credential,
    //! same rejection, every 30s, forever. Ask for a fresh token first, then retry.
    //
    //! RATE-LIMITED, and that is not optional. handleAuthError leaves connectionState
    //! === 'connected' (only isAuthenticated flips), so setUserIdentity() sees
    //! wasConnected and calls refreshConnection() — which RESETS reconnectAttempts to
    //! 0 and reconnects at once. Unbounded, a permanently-rejected credential would
    //! therefore loop at network speed: a fresh auth-service token request plus a new
    //! socket every couple of seconds, forever. That is strictly WORSE than the slow
    //! stale-token loop this emit exists to cure. One request per interval caps the
    //! blast radius on the auth service; the reconnect backoff still runs underneath.
    const nowTs = Date.now();
    if (
      this.lastTokenRefreshRequestAt === null ||
      nowTs - this.lastTokenRefreshRequestAt >= this.tokenRefreshMinIntervalMs
    ) {
      this.lastTokenRefreshRequestAt = nowTs;
      this.notifyListeners('token_refresh_needed', { reason: 'auth_error', message });
    } else {
      console.warn('[WebSocketManager] token refresh already requested recently — not re-asking (auth-service rate limit)');
    }

    //! always schedule — reconnect() picks the fast or slow-indefinite phase.
    this.scheduleReconnect();

    this.notifyListeners('auth_error', message);
  }

  /**
   * Handle pong response for latency measurement
   */
  handlePong(message, receiveTime) {
    if (message.timestamp) {
      const latency = receiveTime - message.timestamp;
      this.updateLatencyMetrics(latency);
    }
    
    // Clear heartbeat timeout
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  /**
   * Update latency metrics with rolling average
   */
  updateLatencyMetrics(latency) {
    this.metrics.lastLatency = latency;
    this.metrics.latencyHistory.push(latency);
    
    // Keep only last 100 measurements
    if (this.metrics.latencyHistory.length > 100) {
      this.metrics.latencyHistory.shift();
    }
    
    // Calculate rolling average
    this.metrics.avgLatency = this.metrics.latencyHistory.reduce((sum, l) => sum + l, 0) 
                             / this.metrics.latencyHistory.length;
  }

  /**
   * Handle hierarchical identity success (BE-001 protocol)
   */
  handleIdentitySuccess(message) {
    console.log('✅ Hierarchical identity established:', {
      session_id: message.session_id,
      user_id: message.user_id,
      device_id: message.device_id,
      window_id: message.window_id,
      page_id: message.page_id
    });

    // Update local identity with confirmed server values
    if (message.session_id) this.userIdentity.session_id = message.session_id;
    
    this.hierarchicalIdentity = {
      user_id: message.user_id || this.userIdentity.user_id,
      device_id: message.device_id || this.userIdentity.device_id,
      window_id: message.window_id || this.userIdentity.window_id,
      page_id: message.page_id || this.userIdentity.page_id
    };

    // Notify listeners that hierarchical identity is ready
    this.notifyListeners('identity_ready', this.hierarchicalIdentity);
    // Set general ready state and fire event
    this.ready = true;
    if (this._readyResolve) {
      this._readyResolve();
      this._readyPromise = null;
      this._readyResolve = null;
    }
    this.notifyListeners('ready', this.hierarchicalIdentity);
  }

  /**
   * Handle hierarchical identity error (BE-001 protocol)
   */
  handleIdentityError(message) {
    console.error('❌ Hierarchical identity setup failed:', message);
    
    // Notify listeners of the error
    this.notifyListeners('identity_error', {
      error: message.error,
      message: message.message
    });

    // Could implement retry logic here
    if (message.error === 'missing_device_id' || message.error === 'missing_window_id') {
      console.log('🔄 Retrying identity setup with regenerated IDs...');
      
      // Regenerate missing IDs
      this.userIdentity.device_id = this.generateDeviceId();
      this.userIdentity.window_id = uuidv4();
      
      // Retry identity setup
      setTimeout(() => {
        this.sendIdentitySetup();
      }, 1000);
    }
  }

  /**
   * Send identity setup message separately (for manual retry)
   */
  sendIdentitySetup() {
    // Use protocol string type so UltraFastProtocol maps to numeric code
    const identityMessage = this.protocol.createMessage({
      type: 'identity',
      device_id: this.userIdentity.device_id,
      window_id: this.userIdentity.window_id,
      page_id: this.generatePageId(),
      timestamp: Date.now()
    });
    this.sendMessage(identityMessage);
  }

  /**
   * Send message with performance optimizations
   */
  sendMessage(message, channel = 'default') {
    const messageWithMetadata = {
      ...message,
      channel,
      session_id: this.userIdentity.session_id,
      timestamp: performance.now()
    };
    
    console.log(`📤 [WebSocketManager] sendMessage: type=${message.t}, connectionState=${this.connectionState}`);
    
    if (this.connectionState !== 'connected') {
      // Queue message for later sending
      console.log(`📤 [WebSocketManager] NOT connected, queuing message (queue size: ${this.pendingMessages.length})`);
      if (this.pendingMessages.length < this.config.maxQueueSize) {
        this.pendingMessages.push(messageWithMetadata);
      }
      return;
    }
    
    try {
      let serializedMessage;
      
      if (this.config.enableBinary && this.protocol.supportsBinary()) {
        serializedMessage = this.protocol.serializeMessage(messageWithMetadata, 'binary');
        console.log(`📤 [WebSocketManager] Sending BINARY message type=${message.t}`);
      } else {
        serializedMessage = this.protocol.serializeMessage(messageWithMetadata, 'text');
        console.log(`📤 [WebSocketManager] Sending TEXT message type=${message.t}`);
      }
      
      this.connection.send(serializedMessage);
      this.metrics.messagesSent++;
      
    } catch (error) {
      console.error('WebSocket send error:', error);
      this.notifyListeners('error', error);
    }
  }

  /**
   * Subscribe to messages on a specific channel
   */
  subscribe(channel, handler) {
    if (!this.messageHandlers.has(channel)) {
      this.messageHandlers.set(channel, new Set());
    }
    this.messageHandlers.get(channel).add(handler);
    // Send subscription message to server using numeric protocol
    this.sendMessage({
      t: MESSAGE_TYPES.SUBSCRIBE,
      d: { channel }
    });
    // Return unsubscribe function
    return () => this.unsubscribe(channel, handler);
  }

  /**
   * Unsubscribe from channel messages
   */
  unsubscribe(channel, handler) {
    const handlers = this.messageHandlers.get(channel);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.messageHandlers.delete(channel);
        // Send unsubscribe message to server using numeric protocol
        this.sendMessage({
          t: MESSAGE_TYPES.UNSUBSCRIBE,
          d: { channel }
        });
      }
    }
  }

  /**
   * Handle WebSocket disconnection and trigger reconnect attempts
   */
  handleDisconnection(event) {
    console.warn('🔌 WebSocket disconnected:', event);
    this.connectionState = 'disconnected';
    this.isAuthenticated = false;
    this.transportOpenedAt = null; //! #44: handshake window is over — socket is gone
    //! #50-1: stamp the FIRST drop of a losing streak only (mirrors
    //! markBackgrounded's "keep the original clock" guard) — a burst of
    //! onclose/error callbacks around one real outage must not keep pushing the
    //! gap start forward, or the reconnect repair would undercount what was missed.
    if (this.disconnectedAt == null) {
      this.disconnectedAt = Date.now();
    }
    this.notifyListeners('connection', { state: 'disconnected', event });
    //! #44: always schedule — reconnect() picks the fast or slow-indefinite
    //! phase. The old max-attempts give-up caused the silent screen-blank.
    this.scheduleReconnect();
    this.ready = false;
    if (this._readyPromise) {
      // Don't resolve, just reset
      this._readyPromise = null;
      this._readyResolve = null;
    }
  }

  /**
   * Reconnect with exponential backoff and jitter
   */
  reconnect() {
    //! #44: only ever ONE reconnect in flight. Guarding on the tracked timer
    //! prevents stacked timers and double-incremented attempts when several
    //! callers (onclose reject + handleDisconnection + auth_error) fire at once.
    if (this.reconnectTimeout) {
      return;
    }
    // Ensure state is set to disconnected before attempting reconnect
    this.connectionState = 'disconnected';
    this.reconnectAttempts++;

    let delay;
    if (this.reconnectAttempts <= this.maxReconnectAttempts) {
      //--> Fast phase: exponential backoff with jitter, capped at maxReconnectDelay.
      const baseDelay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
      const jitter = Math.random() * 1000; // up to 1s of jitter
      delay = Math.min(baseDelay + jitter, this.maxReconnectDelay);
    } else {
      //! #44 SLOW phase: the fast budget is spent but we NEVER give up — keep
      //! retrying indefinitely at slowRetryInterval so a multi-hour outage still
      //! self-heals without a hard refresh. Surface DEGRADED exactly once.
      delay = this.slowRetryInterval + Math.random() * 1000;
      if (!this.isDegraded) {
        this.isDegraded = true;
        console.warn(`⚠️ WebSocket DEGRADED: ${this.maxReconnectAttempts} fast attempts exhausted — now retrying every ${Math.round(this.slowRetryInterval / 1000)}s indefinitely (never giving up).`);
        this.notifyListeners('reconnect_degraded', {
          reason: 'max_fast_attempts_exhausted',
          attempts: this.reconnectAttempts,
        });
      }
    }

    console.log(`🔄 Reconnecting in ${Math.ceil(delay / 1000)}s (attempt ${this.reconnectAttempts}${this.isDegraded ? ', slow/degraded' : ''})...`);
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect().catch(error => {
        console.error('[WebSocketManager] WebSocket reconnection error:', error);
        //! never give up — schedule the next attempt (fast or slow phase).
        this.reconnect();
      });
    }, delay);
  }

  /**
   * Schedule a reconnect attempt. Single entry point used by the disconnect /
   * auth-error handlers — reconnect() owns the timer and the two-phase backoff.
   */
  scheduleReconnect() {
    this.reconnect();
  }

  /**
   * #44: Immediate reconnect used by the online/focus/visibility auto-resume
   * hooks and by the delivery-stall watchdog. Resets the backoff so a
   * network-restore reconnects AT ONCE instead of waiting out the (up to 30s)
   * slow-phase timer. No-op when already healthy.
   */
  forceReconnectNow(reason = 'manual', { force = false, preserveBackoff = false } = {}) {
    //! V12b F1: this early-return made the delivery-stall self-heal a PROVEN
    //! NO-OP. `stalled` is defined as `transportUp && …` where transportUp is
    //! `connectionState === 'connected' && isAuthenticated` — byte-identical to
    //! the condition below. So every single call from the stall watchdog
    //! returned here without reconnecting, while the caller logged
    //! "self-healing" and incremented a heals counter. Four documents asserted
    //! the heal worked; the live 2026-08-04 run corroborates it never did (the
    //! socket gauge held steady at 2.0 — a real reconnect churns it).
    //! The gate is still right for the online/focus/visibility callers, whose
    //! whole point is "don't tear down a healthy socket". A delivery stall is
    //! the one case where connected+authenticated is NOT healthy, so that
    //! caller passes force:true.
    if (!force && this.connectionState === 'connected' && this.isAuthenticated) {
      return; // already healthy — nothing to do
    }
    console.log(`🔄 [WebSocketManager] forceReconnectNow (${reason}) — tearing down any stale socket and reconnecting immediately`);
    //! #44: tear down any in-flight/stale socket FIRST. Without this, connect()
    //! early-returns whenever state is 'connecting' — so a zombie socket created
    //! during an offline window (stuck CONNECTING) would make this a no-op and
    //! pin the banner on "reconnecting" forever. Detach handlers so the old
    //! socket's late close can't fight the fresh connect.
    if (this.connection) {
      try {
        this.connection.onopen = this.connection.onmessage = this.connection.onclose = this.connection.onerror = null;
        this.connection.close();
      } catch (e) { /* ignore */ }
      this.connection = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    //! V12b F1/F8: resetting the backoff is right for the online/focus/visibility
    //! callers — the INPUT genuinely changed (network came back), so retrying at
    //! once is warranted. It is WRONG for the delivery-stall heal: a browser
    //! reconnect cannot make a dead broker adapter produce ticks, so a long
    //! upstream outage would rebuild every tab's socket on a 60s loop for ever,
    //! and clearing isDegraded on each pass means the escalation to the louder
    //! 'degraded' banner could never be reached. That caller passes
    //! preserveBackoff:true so repeated heals still deplete the fast-retry
    //! budget and escalate. See feedback_never_give_up_needs_a_changing_input.
    if (!preserveBackoff) {
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.isDegraded = false;
    }
    //! ensure connect() doesn't early-return on a stale 'connecting'/'connected'.
    this.connectionState = 'disconnected';
    this.connect().catch(error => {
      console.error('[WebSocketManager] forceReconnectNow error:', error);
      this.scheduleReconnect();
    });
  }

  /**
   * #44: is this socket STUCK — i.e. genuinely nothing left that will recover it
   * on its own? Ground truth for the provider's poll rescue.
   *
   * Background: the first cut of that poll simply asked "not connected+authed?"
   * and force-reconnected every 8s. That tore the socket down mid-auth-handshake
   * and reset the backoff on every tick, so it never stabilised. The manager
   * already self-heals three of the four unhealthy states, so the poll must stay
   * out of their way and fire ONLY on the fourth:
   *   - 'connecting'                  -> connect()'s connectTimeout owns it;
   *   - reconnectTimeout pending      -> reconnect()'s backoff owns it;
   *   - connected, auth in flight     -> nothing owns it, but it is NORMAL for
   *                                      the first authGraceMs — only past that
   *                                      window is the handshake really wedged;
   *   - down, nothing scheduled       -> STUCK. Nobody is coming. Rescue.
   *
   * @returns {string|null} the stuck-reason, or null when someone else has it.
   */
  needsRescue(nowTs = Date.now()) {
    if (this.connectionState === 'connected' && this.isAuthenticated) {
      return null; // healthy
    }
    //! No credentials yet => there is nothing to rescue and we must NOT connect.
    //! WebSocketProvider.initializeConnection() awaits a network round-trip for the
    //! WS token BEFORE calling setUserIdentity()/connect(); during that window the
    //! manager sits 'disconnected' with no timer, which looks exactly like "stuck".
    //! Rescuing there would open a socket with NO token — auth fails, and we race
    //! the real connect() that follows. The provider owns that window, not the poll.
    if (!this.authToken) {
      return null;
    }
    //! Deliberate client disconnect: the never-give-up loop must not resurrect a
    //! socket the app intentionally closed (disconnect() cancels its own retry for
    //! exactly that reason). Cleared the moment anything asks to connect again.
    if (this.intentionallyDisconnected) {
      return null;
    }
    if (this.connectionState === 'connecting') {
      return null; // connectTimeout will tear down a stuck CONNECTING
    }
    if (this.reconnectTimeout) {
      return null; // a retry is already scheduled — never reset its backoff
    }
    if (this.connectionState === 'connected' && !this.isAuthenticated) {
      //! transport up, auth round-trip outstanding. Leave it alone until the
      //! grace window is spent — this is the branch that caused the churn.
      const openedAt = this.transportOpenedAt;
      if (openedAt === null || nowTs - openedAt < this.authGraceMs) {
        return null;
      }
      return 'auth-handshake-stalled';
    }
    return 'no-reconnect-scheduled';
  }

  /**
   * Start heartbeat mechanism
   */
  startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    this.heartbeatInterval = setInterval(() => {
      if (this.connectionState !== 'connected' || !this.isAuthenticated) {
        return;
      }
      
      // Send heartbeat (ping) message
      const pingMessage = this.protocol.createMessage({
        type: 'ping',
        timestamp: Date.now()
      });
      
      this.sendMessage(pingMessage);
    }, this.config.heartbeatInterval);
  }

  /**
   * Stop heartbeat mechanism
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Process and send pending messages
   */
  processPendingMessages() {
    while (this.pendingMessages.length > 0) {
      const message = this.pendingMessages.shift();
      this.sendMessage(message);
    }
  }

  /**
   * Notify listeners of connection state changes and errors
   */
  notifyListeners(event, data) {
    // Custom event system to notify about connection state, errors, etc.
    // Can be replaced with a more robust event emitter if needed
    const listeners = this.listeners || {};
    const count = listeners[event] ? listeners[event].length : 0;
    // Always log event name and listener count for debugging
    // console.log(`[WebSocketManager.notifyListeners] Event: '${event}', Listeners: ${count}`);
    if (listeners[event]) {
      listeners[event].forEach(fn => {
        try {
          fn(data);
        } catch (e) {
          console.error(`Error in listener for event "${event}":`, e);
        }
      });
    }
  }

  /**
   * Add event listener for connection events
   */
  on(event, fn) {
    if (!this.listeners) {
      this.listeners = {};
    }
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(fn);
  }

  /**
   * Remove event listener for connection events
   */
  off(event, fn) {
    if (!this.listeners || !this.listeners[event]) {
      return;
    }
    this.listeners[event] = this.listeners[event].filter(f => f !== fn);
  }

  /**
   * Get performance metrics
   */
  getMetrics() {
    return {
      ...this.metrics,
      isConnected: this.connectionState === 'connected',
      uptime: this.metrics.connectTime ? Date.now() - this.metrics.connectTime : 0
    };
  }

  /**
   * #44 V12: classify a topic as a live-tick topic (market.{id}.tick). These
   * are the surfaces where "backend green / screen blank" shows up — the socket
   * is healthy but no ticks reach the subscriber.
   */
  static isLiveTickTopic(topic) {
    return typeof topic === 'string' && /^market\.[^.]+\.tick$/.test(topic);
  }

  /**
   * #44 V12: does the client currently hold at least one live-tick subscription?
   * Used to gate the delivery-stall watchdog — no tick subs => nothing to stall.
   */
  hasActiveTickSubscriptions() {
    for (const [topic, handlers] of this.topicHandlers) {
      if (WebSocketManager.isLiveTickTopic(topic) && handlers && handlers.size > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * V12B-11: the venue hint this topic was subscribed with, or null.
   */
  getTopicExchange(topic) {
    return this.topicExchange.get(topic) || null;
  }

  /**
   * V12B-11: every live tick subscription, paired with the venue that feeds it and
   * when THAT topic last delivered.
   *
   * //! This is the whole point of V12B-11. Before it, the predicate had exactly two
   * //! facts — "some venue is open" and "the newest tick in the app is old" — and those
   * //! two cannot be combined into a truthful statement about any one instrument. A
   * //! watchlist of NSE names past 15:30 satisfies both while being perfectly healthy.
   *
   * //* `lastAt: null` means SUBSCRIBED BUT NEVER DELIVERED, which is not the same as
   * //* "stale" and must never be treated as one — it is the self-calibrating gate that
   * //* stops a brand-new subscription firing before its first tick.
   */
  getTickDeliverySources() {
    const sources = [];
    this.topicHandlers.forEach((handlers, topic) => {
      if (!WebSocketManager.isLiveTickTopic(topic) || !handlers || handlers.size === 0) return;
      const exchange = this.topicExchange.get(topic) || null;
      sources.push({
        topic,
        exchange,
        //! unknown exchange => the union, so a caller we have not threaded the hint
        //! through yet behaves exactly as it did before V12B-11. See isTickVenueOpenNow.
        venueOpen: isTickVenueOpenNow(exchange),
        lastAt: this.topicLastDataAt.has(topic) ? this.topicLastDataAt.get(topic) : null,
      });
    });
    return sources;
  }

  /**
   * #44 V12 delivery-health snapshot. Distinct from transport health: the socket
   * can be connected+authenticated while delivering ZERO ticks to subscribers.
   * `stalled` is true only when we WERE receiving ticks this session and have an
   * active tick subscription but have gone silent past `stallThresholdMs` — this
   * self-calibrating gate avoids false alarms off-market-hours (where no tick
   * ever arrives, so lastInboundTickAt stays null and stalled stays false).
   *
   * @param {number} stallThresholdMs - silence window that counts as a stall
   */
  getDeliveryHealth(stallThresholdMs = DELIVERY_STALL_THRESHOLD_MS) {
    const now = Date.now();
    const hasTickSubs = this.hasActiveTickSubscriptions();
    const lastTickAgeMs = this.lastInboundTickAt ? now - this.lastInboundTickAt : null;
    const lastDataAgeMs = this.lastInboundDataAt ? now - this.lastInboundDataAt : null;
    const transportUp = this.connectionState === 'connected' && this.isAuthenticated;

    //! V12b F2 / V12B-11: silence is only a FAULT while the venue feeding THIS
    //! instrument is actually trading. V12B-01 asked the union of all Indian venues,
    //! which is ~09:00-23:30 on a weekday because MCX is the widest — so an NSE-only
    //! watchlist read its own correct post-15:30 silence as a fault for eight hours.
    //! Now: open iff at least one SUBSCRIBED venue is trading. With no tick topics to
    //! ask (or none carrying a hint) this degrades to exactly the old union.
    const sources = this.getTickDeliverySources();
    const openSources = sources.filter((s) => s.venueOpen);
    const sessionOpen = sources.length > 0 ? openSources.length > 0 : isAnyIndianVenueOpenNow();

    //! V12b: a baseline from a PREVIOUS trading day is not evidence about this
    //! one. Without this, resuming a suspended machine at 09:10 measures the age
    //! from yesterday's last tick and fires instantly, before one tick of the new
    //! session could arrive. Independent of the watchdog's reset, which only runs
    //! if a pass happens to land while the session is shut.
    //! DERIVED from the timestamp itself rather than stamped into a second
    //! field — a parallel field can desync from the value it describes, and a
    //! desynced baseline flag would silently disable the stall watchdog. There
    //! is only one fact here, so store it once.
    const todayKey = istTradingDayKey();
    const isTodaysBaseline = (ts) => ts !== null && istTradingDayKey(new Date(ts)) === todayKey;
    const baselineIsToday = isTodaysBaseline(this.lastInboundTickAt);

    //! V12B-11: judge each OPEN venue against ITS OWN last delivery, never against the
    //! global newest-tick timestamp. The global one is a max across every tick topic, so
    //! in a mixed watchlist it reports the CLOSED venue's stale 15:30 tick and re-arms
    //! the whole defect the moment one MCX row is present. A source that has never
    //! delivered has no baseline and therefore cannot be stalled.
    //! Per-topic recency becomes authoritative as soon as ANY tick topic has delivered;
    //! until then there is nothing per-topic to reason from and the global baseline is
    //! the only evidence available.
    const perTopicKnown = sources.some((s) => s.lastAt !== null);
    const venueStalled = perTopicKnown
      ? openSources.some((s) => isTodaysBaseline(s.lastAt) && (now - s.lastAt) > stallThresholdMs)
      : (this.lastInboundTickAt !== null && baselineIsToday && lastTickAgeMs > stallThresholdMs);

    const stalled = Boolean(
      transportUp &&
      hasTickSubs &&
      sessionOpen &&
      venueStalled
    );

    //! #31/exchange-session-ssot R5.3/R5.4, DK-4: a SEPARATE alarm from the 45s
    //! stall rule above (that rule is untouched). `lastAt === null` means
    //! SUBSCRIBED BUT NEVER DELIVERED (see getTickDeliverySources docstring) —
    //! on a venue MDMS reports as EXPLICITLY open, that silence has no benign
    //! explanation (a fresh subscription still waiting for its first tick
    //! outside market hours is normal; the same silence while MDMS says the
    //! venue is trading is not). Strict `=== true`, not the truthy `venueOpen`
    //! filter used for openSources/sessionOpen above: EXCHANGE_STATUS_UNKNOWN
    //! must NOT arm this alarm (DK-4 — "only when MDMS status is known and OPEN").
    const neverStartedSources = sources.filter((s) => s.lastAt === null && s.venueOpen === true);
    const neverStarted = Boolean(transportUp && hasTickSubs && neverStartedSources.length > 0);

    return {
      transportUp,
      isDegraded: this.isDegraded,
      hasTickSubs,
      tickSubCount: this.getTickSubscriptionCount(),
      lastTickAgeMs,
      lastDataAgeMs,
      sessionOpen,
      baselineIsToday,
      stalled,
      stallThresholdMs,
      //! #31/exchange-session-ssot R5.3/R5.4: never-delivered subscription on an
      //! MDMS-confirmed-OPEN venue. Independent of `stalled` — see the block above.
      neverStarted,
      //! V12B-11 observability: WHICH venue held the session open, and how stale each
      //! one is. Without this a live probe cannot tell "correctly quiet because NSE
      //! shut" from "watchdog gated off", which is the exact ambiguity that let the
      //! union survive review. Read via window.__wsHealth().
      //! venueOpen is tri-state (true/false/EXCHANGE_STATUS_UNKNOWN) since #31 — MDMS-fed.
      tickVenues: sources.map((s) => ({
        topic: s.topic,
        exchange: s.exchange,
        venueOpen: s.venueOpen,
        ageMs: s.lastAt === null ? null : now - s.lastAt,
      })),
    };
  }

  /**
   * #44 V12 (d): forget that ticks ever flowed, so the stall predicate
   * re-calibrates from the next real tick.
   *
   * //! V12b F2: `lastInboundTickAt` is otherwise write-once-per-tick and NEVER
   * //! reset (constructor, then every tick frame). Without this, gating
   * //! `stalled` on the session window is not enough: at the next open the age
   * //! is still measured from YESTERDAY's last tick, so the watchdog would fire
   * //! instantly at 09:00 — before a single tick of the new session has had a
   * //! chance to arrive. The watchdog calls this once when it sees the session
   * //! window close, which restores the "only if ticks flowed THIS session"
   * //! property the predicate's docstring has always claimed.
   *
   * //* Kept OUT of getDeliveryHealth(): that method is called ~30x/min by the
   * //* reconciler and must stay pure/idempotent (it is read on a hot path).
   */
  resetDeliveryBaseline(reason = 'session-closed') {
    let changed = false;

    //! V12B-11: the per-topic baselines are the SAME fact, held per venue. Clearing only
    //! the global one would leave every tick topic holding today's last timestamp, so at
    //! tomorrow's open the per-venue predicate fires instantly against it — reopening
    //! exactly the hole this method exists to close, one layer down.
    Array.from(this.topicLastDataAt.keys()).forEach((topic) => {
      if (WebSocketManager.isLiveTickTopic(topic)) {
        this.topicLastDataAt.delete(topic);
        changed = true;
      }
    });

    if (this.lastInboundTickAt !== null) {
      this.lastInboundTickAt = null;
      changed = true;
    }

    if (changed) console.info(`[WS-DELIVERY] delivery baseline reset (${reason})`);
    return changed;
  }

  /**
   * #44 V12: count of active live-tick subscriptions.
   */
  getTickSubscriptionCount() {
    let count = 0;
    this.topicHandlers.forEach((handlers, topic) => {
      if (WebSocketManager.isLiveTickTopic(topic) && handlers && handlers.size > 0) {
        count += 1;
      }
    });
    return count;
  }

  /**
   * Returns true if WebSocket is fully ready (connected, authenticated, identity established)
   */
  isReady() {
    return this.ready;
  }

  /**
   * Returns a Promise that resolves when ready (for async/await usage)
   */
  waitUntilReady() {
    if (this.ready) return Promise.resolve();
    if (!this._readyPromise) {
      this._readyPromise = new Promise(resolve => {
        this._readyResolve = resolve;
      });
    }
    return this._readyPromise;
  }

  /**
   * Record that the tab went to background. Stamps the clock — nothing else.
   *
   * //! F9: this used to be `pauseNonCriticalSubscriptions()`, which also walked
   * //! every topic and UNSUBSCRIBED the ones `shouldPauseInBackground()` matched.
   * //! It matched NOTHING, ever: that list named `ticks.*` / `orderbook.*` /
   * //! `watchlist-prices` while our tick topic is `market.{uuid}.tick`. So the
   * //! loop ran, matched zero topics, and `pausedSubscriptions` stayed
   * //! permanently empty — which in turn made the resume-side loop a no-op too.
   * //! Both were dead together, so removing them changed no behaviour at all.
   *
   * //! The NAME was the actual damage. It convinced readers (including Deepak,
   * //! and me on first pass) that the app stops streaming to hidden tabs. It does
   * //! not, and never has. Charts freezing while hidden is CHROME'S INTENSIVE
   * //! THROTTLING — measured in-repo at V12B-05: the delivery watchdog advanced
   * //! 9 -> 10 across four hidden minutes. Ticks keep being delivered; the tab is
   * //! simply not running enough JS to render them.
   *
   * //! ⛔ Do NOT re-add pausing here without #50 (gap backfill) landing FIRST.
   * //! Unsubscribing on hide creates a permanent data hole DELIBERATELY on every
   * //! hide — WS is live-only, nothing replays it — instead of occasionally when
   * //! a socket happens to die. It would also make a hidden tab report the
   * //! delivery stall it caused itself, because getDeliveryHealth() still counts
   * //! paused topics as active tick subscriptions.
   */
  markBackgrounded() {
    if (this.isInBackground) return; //--> duplicate visibilitychange; keep the original clock

    this.isInBackground = true;
    this.backgroundSince = Date.now();
    console.log('🌙 [Background] Tab hidden — subscriptions keep streaming; browser throttling applies');
  }

  /**
   * Record that the tab came back, and announce how long it was away.
   *
   * //! The return value and the `tab_foregrounded` event are the ONLY measurement
   * //! of the gap, and are what #50's backfill will size its refetch from. Keep
   * //! them even though the resubscribe loop they used to accompany is gone.
   *
   * @returns {number} milliseconds the tab spent in background (0 if it never was)
   */
  markForegrounded() {
    if (!this.isInBackground) return 0;

    const backgroundDuration = this.backgroundSince ? Date.now() - this.backgroundSince : 0;
    this.isInBackground = false;
    this.backgroundSince = null;

    console.log(`☀️ [Background] Tab visible after ${Math.round(backgroundDuration / 1000)}s`);
    //! #50: anything that missed data while hidden should refetch off this.
    this.notifyListeners('tab_foregrounded', {
      backgroundDuration,
      topics: Array.from(this.topicHandlers.keys()),
    });

    return backgroundDuration;
  }

  /**
   * Get duration in background (for components that need to decide on refresh)
   * @returns {number} Duration in milliseconds, or 0 if not in background
   */
  getBackgroundDuration() {
    if (!this.isInBackground || !this.backgroundSince) {
      return 0;
    }
    return Date.now() - this.backgroundSince;
  }

  /**
   * Check if currently in background mode
   * @returns {boolean}
   */
  isBackgroundMode() {
    return this.isInBackground;
  }
}

// Export singleton instance
export default new WebSocketManager();
